import {
  ALL_REPORT_STATUSES,
  type AllReportStatus,
  REPORT_TYPES,
  type ReportLocation,
  type ReportType,
} from '@felix/domain';
import { ActionIcon, Button, Menu, Select } from '@mantine/core';
import {
  IconAnchor,
  IconAnchorOff,
  IconArrowLeftRight,
  IconArrowRight,
  IconCalendarEvent,
  IconChevronDown,
  IconDots,
  IconDownload,
  IconEye,
  IconFileDescription,
  IconFlag,
  IconPencil,
  IconPlus,
  IconSun,
  IconWaveSine,
  IconWind,
  type TablerIcon,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDateTime } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import { useShell } from '../../shell-context.js';
import table from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import type { PersonnelVessel } from '../personnel/types.js';
import {
  LoadError,
  NoMatch,
  SortableTh,
  type SortState,
  TablePager,
  TableSkeleton,
} from '../purchasing/table.js';
import { downloadFile } from '../reports/xlsx.js';
import { AllReportsCards } from './AllReportsCards.js';
import classes from './AllReportsScreen.module.css';
import {
  type AllReportRow,
  type AllReportsColumn,
  type AllReportsFilter,
  allReportRows,
  filterAllReports,
  locationOptions,
  NO_ALL_REPORTS_FILTER,
  newReportKinds,
  newReportPath,
  reportFormPath,
  reportTypeFromPath,
  sortAllReports,
  toCsv,
  UNASSIGNED,
  voyageLogPath,
} from './all-reports.js';
import { useReportLabels } from './report-labels.js';
import type { VoyageSource } from './source.js';
import type { Voyage, VoyageReport } from './types.js';
import { VoyageStartNotice } from './VoyageContextNotices.js';
import { VoyageRequestsPanel } from './VoyageLinksPanels.js';
import { pendingVoyageStart } from './voyage-start.js';

const PAGE_SIZE = 10;

/** Sıralanan sütunlar ("Key Information"dan önce; Status ayrıca). */
const SORTED_COLUMNS = ['date', 'type', 'location', 'voyage'] as const;
const COLUMN_CLASS: Record<(typeof SORTED_COLUMNS)[number], string> = {
  date: classes.colDate ?? '',
  type: classes.colType ?? '',
  location: classes.colLocation ?? '',
  voyage: classes.colVoyage ?? '',
};

const TYPE_ICON: Record<ReportType, TablerIcon> = {
  arrival: IconAnchor,
  port_daily: IconCalendarEvent,
  departure: IconArrowRight,
  anchoring: IconAnchor,
  anchor_aweigh: IconAnchorOff,
  drifting: IconWind,
  drift_departure: IconWaveSine,
  canal_strait: IconArrowLeftRight,
  noon: IconSun,
  other: IconFlag,
};

const STATUS_TONE: Record<AllReportStatus, StatusTone> = {
  draft: 'warning',
  submitted: 'success',
  amended: 'info',
  reopened: 'warning',
  void: 'danger',
};

export type AllReportsLoad =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | {
      kind: 'ready';
      vessels: PersonnelVessel[];
      vessel: PersonnelVessel | null;
      voyages: Voyage[];
      reports: VoyageReport[];
    };

/**
 * Raporlar tek okumada. Eski gemi sunucusunda (uç yok) seferlerin raporları ve sefersiz
 * kalkışlar birleştirilir: liste yine tamdır (ADR 0082).
 */
async function loadReports(source: VoyageSource, vessel: PersonnelVessel, voyages: Voyage[]) {
  if (source.allReports) {
    try {
      return await source.allReports(vessel.id);
    } catch (error) {
      if (errorCode(error) !== 'NOT_FOUND') throw error;
    }
  }
  const lists = await Promise.all([
    ...voyages.map((voyage) => source.reports(voyage.id)),
    source.departures ? source.departures(vessel.id) : Promise.resolve([]),
  ]);
  return [...new Map(lists.flat().map((report) => [report.id, report])).values()];
}

export interface AllReportsScreenProps {
  source: VoyageSource;
  /** Tür süzgeci; verilmezse menü yolundan (`/voyage/reports/<tür>`). */
  type?: ReportType | null;
}

/**
 * Voyage ▸ Reports ▸ All Reports (ADR 0082; ortağın `sefer-6.jpeg`, `sefer-all-reports.jpeg`):
 * geminin bütün sefer raporları kronolojik, seferli ve sefersiz. Süzgeçler, Export (CSV), "Add New
 * Report" mevcut rapor akışlarına gider. Menüdeki dokuz rapor öğesi bu ekranı tür süzgeciyle açar.
 * Gemide ve ofiste aynı ekran (ADR 0019 §2); ofiste salt okunur, gemi seçilir.
 */
export function AllReportsScreen({ source, type }: AllReportsScreenProps) {
  const shell = useShell();
  // Ofiste bağlantıdaki gemi (`?vessel=`; sefer talebinden dönüş, ADR 0083 §5)
  const [vesselId, setVesselId] = useState<string | null>(() =>
    new URLSearchParams(shell?.search ?? '').get('vessel'),
  );
  const [load, setLoad] = useState<AllReportsLoad>({ kind: 'loading' });

  const read = useCallback(async () => {
    setLoad({ kind: 'loading' });
    try {
      const vessels = await source.vessels();
      const vessel = vessels.find((v) => v.id === vesselId) ?? vessels[0] ?? null;
      if (!vessel) {
        setLoad({ kind: 'ready', vessels, vessel, voyages: [], reports: [] });
        return;
      }
      const voyages = await source.voyages(vessel.id);
      const reports = await loadReports(source, vessel, voyages);
      setLoad({ kind: 'ready', vessels, vessel, voyages, reports });
    } catch (error) {
      setLoad({ kind: 'error', code: errorCode(error) });
    }
  }, [source, vesselId]);
  useEffect(() => {
    void read();
  }, [read]);

  const pending = load.kind === 'ready' ? pendingVoyageStart(load.reports, load.voyages) : null;

  const exportCsv = async (fileName: string, csv: string) => {
    const bytes = new TextEncoder().encode(`\uFEFF${csv}`);
    if (source.download) await source.download(fileName, bytes, 'text/csv');
    else downloadFile(fileName, bytes, 'text/csv;charset=utf-8');
  };

  return (
    <AllReportsView
      load={load}
      scope={source.scope}
      canCreate={!!source.editor}
      initialType={type !== undefined ? type : reportTypeFromPath(shell?.path ?? '')}
      onVessel={setVesselId}
      onRetry={() => void read()}
      onExport={exportCsv}
      onNavigate={(path) => shell?.navigate(path)}
      notice={
        // Seferi başlatılmamış kalkış (ADR 0157 §2): gemide eylemler, ofiste salt okunur + talep
        pending && load.kind === 'ready' && load.vessel ? (
          <VoyageStartNotice
            pending={pending}
            onStart={
              source.planner
                ? () =>
                    shell?.navigate(
                      `/voyage/new?departure=${pending.departure.id}&back=${encodeURIComponent(
                        '/voyage/reports',
                      )}`,
                    )
                : null
            }
            onOpen={() =>
              shell?.navigate(
                source.editor
                  ? `/voyage/legs?voyage=none&report=${pending.departure.id}`
                  : `/voyage/legs?vessel=${load.vessel?.id ?? ''}&voyage=none&report=${pending.departure.id}`,
              )
            }
            onRequest={
              !source.planner && source.requestChange
                ? () =>
                    shell?.navigate(
                      `/voyage/new?vessel=${load.vessel?.id ?? ''}&departure=${pending.departure.id}`,
                    )
                : null
            }
          />
        ) : null
      }
      requests={
        load.kind === 'ready' && load.vessel ? (
          <VoyageRequestsPanel
            source={source}
            vesselId={load.vessel.id}
            voyages={load.voyages}
            onChanged={() => void read()}
          />
        ) : null
      }
    />
  );
}

export interface AllReportsViewProps {
  load: AllReportsLoad;
  scope: 'web' | 'ship';
  /** Gemide rapor yazılır: "Add New Report" ve "Edit". */
  canCreate: boolean;
  initialType: ReportType | null;
  onVessel: (id: string) => void;
  onRetry: () => void;
  onExport: (fileName: string, csv: string) => Promise<void>;
  onNavigate: (path: string) => void;
  /** Saat (Reports by Type; testlerde sabit). */
  now?: () => Date;
  /** Ofisin sefer talepleri kartı (ADR 0083 §5); talep yoksa boş. */
  requests?: ReactNode;
  /** Sayfanın üstündeki bildirim ("Sefer başlatılmadı"; ADR 0157 §2). */
  notice?: ReactNode;
}

/** Ekranın görünümü: süzgeçler, sıralama ve sayfa burada; veri dışarıdan (test edilebilir). */
export function AllReportsView({
  load,
  scope,
  canCreate,
  initialType,
  onVessel,
  onRetry,
  onExport,
  onNavigate,
  now,
  requests,
  notice,
}: AllReportsViewProps) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`voyage.allReports.${key}`, options);
  const initial = useMemo(() => ({ ...NO_ALL_REPORTS_FILTER, type: initialType }), [initialType]);
  const [filter, setFilter] = useState<AllReportsFilter>(initial);
  const [sort, setSort] = useState<SortState<AllReportsColumn>>({ key: 'date', dir: 'desc' });
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const ready = load.kind === 'ready' ? load : null;
  const rows = useMemo(() => (ready ? allReportRows(ready.reports, ready.voyages) : []), [ready]);
  const vesselId = ready?.vessel?.id ?? null;

  const labels = useReportLabels();
  const { locationLabel, statusLabel } = labels;
  const typeLabel = (row: AllReportRow) => labels.typeLabel(row.report.kind, row.type);
  const optionLabel = (location: ReportLocation) =>
    location.type === 'sea' || location.type === 'anchorage'
      ? k(`locations.${location.type}`)
      : locationLabel(location);
  const keyInfo = (row: AllReportRow) => labels.keyInfo(row.keyInfo);
  const voyageLabel = (row: AllReportRow) => row.voyage?.voyageNo ?? '—';

  // Etiketler dilden gelir; liste her çizimde süzülür ve sıralanır (en çok birkaç yüz satır)
  const shown = sortAllReports(filterAllReports(rows, filter), sort, (row, key) =>
    key === 'type'
      ? typeLabel(row)
      : key === 'location'
        ? locationLabel(row.location)
        : key === 'voyage'
          ? voyageLabel(row)
          : statusLabel(row.status),
  );
  const pages = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = shown.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const change = (next: Partial<AllReportsFilter>) => {
    setFilter((f) => ({ ...f, ...next }));
    setPage(1);
  };
  const editable = (report: VoyageReport) =>
    canCreate &&
    (report.status === 'draft' || report.status === 'reopened') &&
    (report.can?.edit ?? true) !== false;

  const exportRows = async () => {
    setExporting(true);
    try {
      const head = [
        k('columns.reportNo'),
        k('columns.date'),
        k('columns.type'),
        k('columns.location'),
        k('columns.voyage'),
        k('columns.keyInfo'),
        k('columns.status'),
      ];
      const body = shown.map((row) => [
        row.report.reportNo ?? '',
        formatDateTime(row.report.reportedAt, 0),
        typeLabel(row),
        locationLabel(row.location),
        row.voyage?.voyageNo ?? k('unassigned'),
        keyInfo(row),
        statusLabel(row.status),
      ]);
      const vessel = (ready?.vessel?.name ?? 'vessel').replace(/[^\w-]+/g, '-');
      await onExport(`all-reports-${vessel}.csv`, toCsv(head, body));
    } finally {
      setExporting(false);
    }
  };

  const voyageOptions = [
    { value: '', label: k('allVoyages') },
    { value: UNASSIGNED, label: k('unassigned') },
    ...(ready?.voyages ?? []).map((voyage) => ({ value: voyage.id, label: voyage.voyageNo })),
  ];
  const locations = locationOptions(rows);
  const actions = (row: AllReportRow, touch = false) => {
    const no = row.report.reportNo || typeLabel(row);
    const size = touch ? 'xl' : 'md';
    return (
      <>
        <ActionIcon
          variant="subtle"
          size={size}
          aria-label={k('view', { no })}
          title={k('viewTitle')}
          onClick={() => onNavigate(reportFormPath(row, scope === 'web' ? vesselId : null))}
        >
          <IconEye size={18} />
        </ActionIcon>
        {editable(row.report) ? (
          <ActionIcon
            variant="subtle"
            size={size}
            aria-label={k('edit', { no })}
            title={k('editTitle')}
            onClick={() => onNavigate(reportFormPath(row, null))}
          >
            <IconPencil size={18} />
          </ActionIcon>
        ) : touch ? null : (
          <span className={classes.actionGap} aria-hidden />
        )}
        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <ActionIcon variant="subtle" size={size} aria-label={k('more', { no })}>
              <IconDots size={18} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              onClick={() => onNavigate(voyageLogPath(row, scope === 'web' ? vesselId : null))}
            >
              {row.voyage ? k('openLog', { no: row.voyage.voyageNo }) : k('openUnassigned')}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </>
    );
  };

  const createMenu = canCreate ? (
    <Menu position="bottom-end" withinPortal>
      <Menu.Target>
        <Button leftSection={<IconPlus size={16} />} rightSection={<IconChevronDown size={14} />}>
          {k('addNew')}
        </Button>
      </Menu.Target>
      <Menu.Dropdown className={classes.createMenu}>
        {newReportKinds(filter.type).map((group) => (
          <div key={group.type}>
            <Menu.Label>{k(`types.${group.type}`)}</Menu.Label>
            {group.kinds.map((kind) => (
              <Menu.Item key={kind} onClick={() => onNavigate(newReportPath(kind))}>
                {labels.typeLabel(kind, group.type)}
              </Menu.Item>
            ))}
          </div>
        ))}
      </Menu.Dropdown>
    </Menu>
  ) : null;

  return (
    <div className={classes.layout}>
      <PageHeader
        title={k('title')}
        description={k('description')}
        icon={<IconFileDescription size={28} stroke={1.6} />}
        actions={
          <div className={classes.headerActions}>
            {createMenu}
            <Button
              variant="default"
              leftSection={<IconDownload size={16} />}
              loading={exporting}
              disabled={!ready || shown.length === 0}
              onClick={() => void exportRows()}
            >
              {k('export')}
            </Button>
          </div>
        }
      />
      {notice}

      <div className={classes.main}>
        <Panel flush>
          <form
            className={classes.filters}
            aria-label={k('filters')}
            onSubmit={(event) => event.preventDefault()}
          >
            {scope === 'web' ? (
              <Select
                label={k('vessel')}
                data={(ready?.vessels ?? []).map((v) => ({ value: v.id, label: v.name }))}
                value={vesselId}
                allowDeselect={false}
                placeholder={k('noVessel')}
                onChange={(value) => {
                  if (value) {
                    onVessel(value);
                    change({ voyage: null, location: null });
                  }
                }}
              />
            ) : null}
            <DayInput
              label={k('dateFrom')}
              placeholder={k('anyDate')}
              value={filter.from}
              maxDate={filter.to ?? undefined}
              onChange={(value) => change({ from: value })}
            />
            <DayInput
              label={k('dateTo')}
              placeholder={k('anyDate')}
              value={filter.to}
              minDate={filter.from ?? undefined}
              onChange={(value) => change({ to: value })}
            />
            <Select
              label={k('reportType')}
              data={[
                { value: '', label: k('allTypes') },
                ...REPORT_TYPES.map((value) => ({ value, label: k(`types.${value}`) })),
              ]}
              value={filter.type ?? ''}
              allowDeselect={false}
              onChange={(value) => change({ type: (value || null) as ReportType | null })}
            />
            <Select
              label={k('voyage')}
              data={voyageOptions}
              value={filter.voyage ?? ''}
              allowDeselect={false}
              onChange={(value) => change({ voyage: value || null })}
            />
            <Select
              label={k('location')}
              data={[
                { value: '', label: k('allLocations') },
                ...locations.map((option) => ({
                  value: option.value,
                  label: optionLabel(option.location),
                })),
              ]}
              value={
                filter.location && locations.some((o) => o.value === filter.location)
                  ? filter.location
                  : ''
              }
              allowDeselect={false}
              searchable
              onChange={(value) => change({ location: value || null })}
            />
            <Select
              label={k('status')}
              data={[
                { value: '', label: k('allStatuses') },
                ...ALL_REPORT_STATUSES.map((value) => ({ value, label: statusLabel(value) })),
              ]}
              value={filter.status ?? ''}
              allowDeselect={false}
              onChange={(value) => change({ status: (value || null) as AllReportStatus | null })}
            />
            <Button
              variant="default"
              className={classes.reset}
              onClick={() => change(NO_ALL_REPORTS_FILTER)}
            >
              {k('reset')}
            </Button>
          </form>
        </Panel>

        <Panel flush>
          {load.kind === 'error' ? (
            <LoadError code={load.code} onRetry={onRetry} />
          ) : load.kind === 'loading' ? (
            <TableSkeleton columns={8} label={k('table')} />
          ) : rows.length === 0 ? (
            <EmptyState
              title={k(ready?.vessel ? 'emptyTitle' : 'noVessel')}
              description={k(scope === 'ship' ? 'emptyShip' : 'emptyWeb')}
            />
          ) : shown.length === 0 ? (
            <NoMatch title={k('noMatch')} onClear={() => change(NO_ALL_REPORTS_FILTER)} />
          ) : (
            <>
              <section
                className={`${table.tableWrap} ${classes.tableBox}`}
                aria-label={k('table')}
                // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
                tabIndex={0}
              >
                <table className={`${table.table} ${classes.table}`}>
                  <thead>
                    <tr>
                      <th scope="col" className={classes.colNo}>
                        {k('columns.no')}
                      </th>
                      {SORTED_COLUMNS.map((column) => (
                        <SortableTh
                          key={column}
                          column={column}
                          label={k(`columns.${column}`)}
                          sort={sort}
                          onSort={setSort}
                          className={COLUMN_CLASS[column]}
                        />
                      ))}
                      <th scope="col">{k('columns.keyInfo')}</th>
                      <SortableTh
                        column="status"
                        label={k('columns.status')}
                        sort={sort}
                        onSort={setSort}
                        className={classes.colStatus ?? ''}
                      />
                      <th scope="col" className={classes.colActions}>
                        {k('columns.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((row, index) => {
                      const Icon = TYPE_ICON[row.type];
                      const location = locationLabel(row.location);
                      const info = keyInfo(row);
                      return (
                        <tr key={row.report.id}>
                          <td className={classes.colNo}>{(current - 1) * PAGE_SIZE + index + 1}</td>
                          <td>{formatDateTime(row.report.reportedAt, 0)}</td>
                          <td>
                            <span className={classes.typeCell} title={typeLabel(row)}>
                              <span className={classes.typeIcon} data-type={row.type} aria-hidden>
                                <Icon size={14} stroke={2} />
                              </span>
                              <span>{typeLabel(row)}</span>
                            </span>
                          </td>
                          <td title={location}>{location}</td>
                          <td>{voyageLabel(row)}</td>
                          <td title={info || undefined}>{info || '—'}</td>
                          <td>
                            <StatusBadge tone={STATUS_TONE[row.status]}>
                              {statusLabel(row.status)}
                            </StatusBadge>
                          </td>
                          <td>
                            <span className={classes.actions}>{actions(row)}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </section>
              <ul className={classes.cards} aria-label={k('table')}>
                {visible.map((row) => {
                  const Icon = TYPE_ICON[row.type];
                  const info = keyInfo(row);
                  return (
                    <li key={row.report.id} className={classes.card}>
                      <div className={classes.cardHead}>
                        <span className={classes.typeCell}>
                          <span className={classes.typeIcon} data-type={row.type} aria-hidden>
                            <Icon size={14} stroke={2} />
                          </span>
                          <strong>{typeLabel(row)}</strong>
                        </span>
                        <StatusBadge tone={STATUS_TONE[row.status]}>
                          {statusLabel(row.status)}
                        </StatusBadge>
                      </div>
                      <dl className={classes.cardFacts}>
                        <dt>{k('columns.date')}</dt>
                        <dd>{formatDateTime(row.report.reportedAt, 0)}</dd>
                        <dt>{k('columns.location')}</dt>
                        <dd>{locationLabel(row.location)}</dd>
                        <dt>{k('columns.voyage')}</dt>
                        <dd>{voyageLabel(row)}</dd>
                        {info ? (
                          <>
                            <dt>{k('columns.keyInfo')}</dt>
                            <dd>{info}</dd>
                          </>
                        ) : null}
                      </dl>
                      <div className={classes.cardActions}>{actions(row, true)}</div>
                    </li>
                  );
                })}
              </ul>
              <TablePager
                page={current}
                pages={pages}
                summary={k('showing', {
                  from: (current - 1) * PAGE_SIZE + 1,
                  to: Math.min(current * PAGE_SIZE, shown.length),
                  total: shown.length,
                })}
                label={k('pagination')}
                onPage={setPage}
              />
            </>
          )}
        </Panel>
        {requests}
        {ready?.vessel ? (
          <AllReportsCards
            scope={scope}
            canCreate={canCreate}
            vesselId={vesselId}
            voyages={ready.voyages}
            reports={ready.reports}
            onNavigate={onNavigate}
            {...(now ? { now } : {})}
          />
        ) : null}
      </div>
    </div>
  );
}
